// Create-tag modal — v3.11.
//
// Opens from the "+ Create tag" affordance on the detail page's
// Tags row. Lets the user name a tag and flip the regulatory
// toggle (which colors the chip ember-tinted everywhere it's
// shown). Slug is derived server-side from the display name.
// On success the caller receives the created tag and immediately
// adds it to the current Commitment.

import { useEffect, useRef, useState } from "react";

import type { CreatedTag } from "./api";


export interface CreateTagModalProps {
  open: boolean;
  /** Pre-fill the display name from whatever the user typed in
   * the picker's search box. */
  initialName?: string;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  onCreate: (input: {
    display_name: string;
    is_regulatory: boolean;
  }) => void;
  /** When the BE returns a row (existing or fresh), the caller
   * can use this to immediately attach the tag to the current
   * Commitment without re-issuing a search. */
  onCreated?: (tag: CreatedTag) => void;
}


export function CreateTagModal({
  open, initialName, isPending, error, onCancel, onCreate,
}: CreateTagModalProps) {
  const [name, setName] = useState(initialName ?? "");
  const [isRegulatory, setIsRegulatory] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setIsRegulatory(false);
      return;
    }
    setName(initialName ?? "");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, initialName]);

  if (!open) return null;
  const trimmed = name.trim();
  const blank = trimmed.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-tag-title"
      data-testid="create-tag-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-text-primary/40"
    >
      <div className="flex w-full max-w-sm flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-4 shadow-lg">
        <h2
          id="create-tag-title"
          className="text-sm font-semibold text-text-primary"
        >
          Create tag
        </h2>

        <label
          className="flex flex-col gap-1 text-xs font-medium text-text-secondary"
          htmlFor="create-tag-name"
        >
          Tag name
          <input
            ref={inputRef}
            id="create-tag-name"
            type="text"
            data-testid="create-tag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ERISA"
            className="rounded-md border border-border-subtle bg-canvas/40 px-2 py-1.5 text-sm text-text-primary"
          />
        </label>

        <label
          className="flex items-center gap-2 text-xs text-text-secondary"
          htmlFor="create-tag-regulatory"
        >
          <input
            id="create-tag-regulatory"
            type="checkbox"
            data-testid="create-tag-regulatory"
            checked={isRegulatory}
            onChange={(e) => setIsRegulatory(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span className="text-text-primary">Regulatory</span>
          <span className="text-text-tertiary">
            — Commitments tagged with this trigger reviewer prompts
          </span>
        </label>

        {error && (
          <p
            role="alert"
            data-testid="create-tag-error"
            className="rounded-md bg-ember-tint px-2 py-1 text-[11px] text-ember"
          >
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="create-tag-cancel"
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="create-tag-submit"
            disabled={blank || isPending}
            onClick={() =>
              onCreate({
                display_name: trimmed,
                is_regulatory: isRegulatory,
              })
            }
            className="rounded-md bg-teal px-3 py-1 text-xs font-semibold text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
